import { DialogService } from "@/service/DialogService";
import { UtilService } from "@/service/UtilService";
import { useSignalStore } from "@/store/signal";
import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import queryString from "query-string";

export class BaseApi {
  /**
   * Axiosのクライアントを返します
   * @returns
   */
  private getClient(): AxiosInstance {
    const signalStore = useSignalStore();
    return axios.create({
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      responseType: "json",
      withCredentials: true,
      signal: signalStore.controller.signal, // AbortControllerとAxiosの紐付け
      // timeout: 30000
    });
  }

  private getFileClient(): AxiosInstance {
    return axios.create({
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      responseType: 'blob',
      withCredentials: true,
    })
  }

  /**
   * GETで実行します
   * @param url
   * @param query
   * @returns
   */
  protected async get<T>(
    url: string,
    query: Record<string, any> | null = null
  ): Promise<AxiosResponse<T>> {
    const client = this.getClient();
    try {
      if (query !== null) {
        url = queryString.stringifyUrl({
          url: url,
          query: query,
        });
      }
      const res = await client.get(url);
      return res;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const err = e as AxiosError<any>;
        await this.httpError(err);
      }
      throw e;
    }
  }

  /**
   * POSTで実行します
   * @param url
   * @param params
   * @returns
   */
  protected async post<T>(url: string, params: any): Promise<AxiosResponse<T>> {
    const client = this.getClient();
    try {
      const res = await client.post(url, params);
      return res;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const err = e as AxiosError<any>;
        await this.httpError(err);
      }
      throw e;
    }
  }

  /**
   * POSTでファイルを取得します
   * @param url
   * @param params
   * @returns
   */
  protected async getBlobFromPost<T>(url: string, params: any): Promise<AxiosResponse<T>> {
    const client = this.getFileClient()
    try {
      const res = await client.post(url, params)
      return res
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const err = e as AxiosError<any>
        await this.httpError(err)
      }
      throw e;
    }
  }

  /**
   * PUTで実行します
   * @param url
   * @param params
   * @returns
   */
  protected async put<T>(url: string, params?: any): Promise<AxiosResponse<T>> {
    const client = this.getClient();
    try {
      const res = await client.put(url, params);
      return res;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const err = e as AxiosError<any>;
        await this.httpError(err);
      }
      throw e;
    }
  }

  /**
   * DELETEで実行します
   * @param url
   * @param params
   * @returns
   */
  protected async delete<T>(
    url: string,
    params: any | null = null
  ): Promise<AxiosResponse<T>> {
    const client = this.getClient();
    try {
      const res = await client.delete(url, { data: params });
      return res;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const err = e as AxiosError<any>;
        await this.httpError(err);
      }
      throw e;
    }
  }

  /**
   * エラー時の処理
   *
   * @param err
   */
  private async httpError(err: AxiosError<any>) {
    if (err.code) {
      const baseMessage = "通信状況の良い場所で再度お試しください。<br>画面に何も表示されない場合はブラウザを更新するか、アプリ設定画面よりキャッシュのクリアをお試しください。"
      const isTimeOut = err.code === "ECONNABORTED" ?? false
      const isNetworkError = err.code === "ERR_NETWORK" ?? false

      if (isNetworkError) {
        await DialogService.error(
          {
            html: "通信に失敗しました。<br>" + baseMessage
          },
          true
        );
        return;
      } else if (isTimeOut) {
        await DialogService.error(
          {
            html: "通信がタイムアウトしました。<br>" + baseMessage
          },
          true
        );
        return;
      }
    }
    if (err.response) {
      if (err.response.status === 401) {
        // 認証エラーだった場合はダイアログを出す
        await UtilService.showAuthErrorDialog();
      } else if (err.response.status === 422) {
        // 422はバリデーションエラーなのでスルー
      } else if (err.response.data) {
        await DialogService.error(
          {
            html: err.response.data.message,
          },
          true
        );
      }
    }
  }
}
